import { uid } from '../../../../lib/uid';

/**
 * Test data backing the Autogenerate button on the position form.
 *
 * Fills every required field so the primary action enables on the first try. Keep it in
 * lockstep with the form: a new required field must be added here in the same change, or
 * Autogenerate silently stops working.
 *
 * Values are synthetic. Never a real person's designation copied from a production plantilla
 * — a generated record is written to the database, so whatever is here ends up in the system.
 */

/** Generic civil-service designations. None of them names a real post at a real employer. */
const DESIGNATIONS = [
  'Administrative Aide',
  'Administrative Officer',
  'Project Evaluation Officer',
  'Records Officer',
  'Supply Officer',
  'Information Systems Analyst',
  'Statistician',
  'Planning Officer',
];

const ROMAN = ['I', 'II', 'III', 'IV', 'V'];

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

/** Six characters of a uid, which is enough to keep repeated clicks from colliding. */
function suffix(): string {
  return uid().slice(0, 6).toLowerCase();
}

export interface SamplePosition {
  title: string;
  code: string;
}

/**
 * One catalog title — the two required fields, and nothing else.
 *
 * Both carry a suffix: each is unique among the ACTIVE rows, and Autogenerate is most useful
 * clicked repeatedly — without one the second click would produce a title the server rejects
 * as a duplicate.
 *
 * The grade is deliberately not here. It is optional, and picking one honestly means picking
 * from the schedule the form has already loaded rather than inventing a pair — so the form
 * does that, where the catalog is. The description is left alone for the plainer reason that
 * filling an optional prose field puts words on the record that nobody wrote.
 */
export function samplePosition(): SamplePosition {
  const unique = suffix();

  return {
    title: `${pick(DESIGNATIONS)} ${pick(ROMAN)} ${unique}`,
    code: `POS-${unique.toUpperCase()}`,
  };
}
